/**
 * CustomerPortal — Concern 1 (frontend-foundations)
 * Actor: customer (self-service)
 * Route: /portal
 * API: GET /api/v1/auth/me, GET /api/v1/customers/lookup?phone=|customer_number=,
 *      GET /api/v1/customers/:id, /:id/payments, /:id/dmrv, /:id/contracts
 *
 * Customer access is permission-scoped by the API. Individual sections
 * degrade gracefully when an account does not hold the required permission.
 */
import { useEffect, useState } from "react";
import { AppAlert, AppButton, AppCard, AppField, AppInput, AppSelect } from "../../../shared/components/ui";
import { IconError, IconInfo, IconSpinner } from "../../../shared/components/icons";
import { api, ApiError } from "../../../shared/api/client";
import { getUser } from "../../auth/session";
import { normalizeTzPhone } from "../../../shared/phone";

type MeResponse = {
  username: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
};

type LookupResult = {
  id: number;
  customer_number: string;
  name: string;
  phone: string;
  active_contract: { id: number; device_id: number | null; amount_remaining: number; arrears: number } | null;
};

type CustomerDetail = {
  id: number;
  customer_number: string;
  first_name: string;
  last_name: string;
  phone: string;
  village: string | null;
  ward: string | null;
  district: string | null;
  region: string | null;
  dmrv_status: string | null;
  dmrv_current_step: number | null;
  devices: { id: number; serial_number: string; status: string; is_locked: boolean; paid_through?: string | null; days_remaining?: number | null }[];
  active_contract: {
    contract_number: string;
    total_price: number;
    final_price: number;
    amount_paid: number;
    amount_remaining: number;
    arrears: number;
    is_completed: boolean;
    start_date: string | null;
  } | null;
  total_paid: number;
};

type PaymentRow = {
  id: number;
  payment_reference: string;
  amount: number;
  method: string;
  status: string;
  device_serial: string | null;
  created_at: string;
};

type DmrvStep = {
  step: number;
  label: string;
  status: string;
  actioned_at: string | null;
  notes: string | null;
};

type ContractRow = {
  id: number;
  contract_number: string;
  total_price: number;
  final_price: number;
  amount_paid: number;
  amount_remaining: number;
  arrears: number;
  payment_frequency: string;
  is_completed: boolean;
  start_date: string | null;
};

type Tab = "profile" | "payments" | "dmrv" | "contracts";
type SectionState = "idle" | "loading" | "error" | "forbidden" | "success";

function friendlyError(ex: unknown, fallback: string): { state: SectionState; message: string } {
  if (ex instanceof ApiError && ex.status === 403) {
    return { state: "forbidden", message: "Your account doesn't have permission to view this yet. Ask your agent for help." };
  }
  if (ex instanceof ApiError && ex.status === 404) {
    return { state: "error", message: "Not found for this account." };
  }
  return { state: "error", message: ex instanceof Error ? ex.message : fallback };
}

export function CustomerPortal() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meError, setMeError] = useState("");

  const [phone, setPhone] = useState("");
  const [customerNumber, setCustomerNumber] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [account, setAccount] = useState<LookupResult | null>(null);

  const [tab, setTab] = useState<Tab>("profile");

  const [profile, setProfile] = useState<CustomerDetail | null>(null);
  const [profileState, setProfileState] = useState<SectionState>("idle");
  const [profileMsg, setProfileMsg] = useState("");

  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsState, setPaymentsState] = useState<SectionState>("idle");
  const [paymentsMsg, setPaymentsMsg] = useState("");

  const [dmrvSteps, setDmrvSteps] = useState<DmrvStep[]>([]);
  const [dmrvState, setDmrvState] = useState<SectionState>("idle");
  const [dmrvMsg, setDmrvMsg] = useState("");

  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [contractsState, setContractsState] = useState<SectionState>("idle");
  const [contractsMsg, setContractsMsg] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("mobile_money");
  const [providerReference, setProviderReference] = useState("");
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState("");

  const findAccount = async (phoneValue: string, customerNumberValue: string) => {
    const params = new URLSearchParams(
      customerNumberValue.trim()
        ? { customer_number: customerNumberValue.trim() }
        : { phone: normalizeTzPhone(phoneValue) },
    );
    const d = await api.get<{ customer: LookupResult }>(`/customers/lookup?${params}`);
    setAccount(d.customer);
    setProfileState("idle"); setPaymentsState("idle"); setDmrvState("idle"); setContractsState("idle");
    setTab("profile");
  };

  useEffect(() => {
    (async () => {
      const storedPhone = getUser()?.phone ?? "";
      if (storedPhone) setPhone(normalizeTzPhone(storedPhone));
      let ownPhone = storedPhone;
      try {
        const d = await api.get<MeResponse>("/auth/me");
        setMe(d);
        ownPhone = d.phone || storedPhone;
      } catch (ex: unknown) {
        setMeError(ex instanceof Error ? ex.message : "Failed to load your account");
      }
      if (ownPhone) {
        const normalized = normalizeTzPhone(ownPhone);
        setPhone(normalized);
        setLookupBusy(true);
        try {
          await findAccount(normalized, "");
        } catch (ex: unknown) {
          if (!(ex instanceof ApiError && ex.status === 404)) {
            setLookupError(ex instanceof Error ? ex.message : "Automatic account lookup failed");
          }
        } finally {
          setLookupBusy(false);
        }
      }
    })();
  }, []);

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() && !customerNumber.trim()) {
      setLookupError("Enter your phone number or customer number");
      return;
    }
    setLookupBusy(true);
    setLookupError("");
    try {
      await findAccount(phone, customerNumber);
    } catch (ex: unknown) {
      if (ex instanceof ApiError && ex.status === 404) {
        setLookupError("No customer found with that phone number or customer number. Ask your agent for your customer number.");
      } else {
        setLookupError(ex instanceof Error ? ex.message : "Lookup failed");
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const loadProfile = async () => {
    if (!account) return;
    setProfileState("loading");
    try {
      const d = await api.get<{ customer: CustomerDetail }>(`/customers/${account.id}`);
      setProfile(d.customer);
      setProfileState("success");
    } catch (ex: unknown) {
      const { state, message } = friendlyError(ex, "Failed to load your profile");
      setProfileState(state); setProfileMsg(message);
    }
  };

  const loadPayments = async () => {
    if (!account) return;
    setPaymentsState("loading");
    try {
      let rows: PaymentRow[] = [];
      try {
        const d = await api.get<{ payments: PaymentRow[] }>(`/customers/${account.id}/payments`);
        rows = d.payments ?? [];
      } catch {
        rows = [];
      }
      if (rows.length === 0) {
        const d2 = await api.get<{ payments: PaymentRow[] }>(
          `/payments?customer_id=${account.id}&per_page=100`,
        );
        rows = d2.payments ?? [];
      }
      setPayments(rows);
      setPaymentsState("success");
    } catch (ex: unknown) {
      const { state, message } = friendlyError(ex, "Failed to load your payment history");
      setPaymentsState(state); setPaymentsMsg(message);
    }
  };

  const loadDmrv = async () => {
    if (!account) return;
    setDmrvState("loading");
    try {
      const d = await api.get<{ steps: DmrvStep[] }>(`/customers/${account.id}/dmrv`);
      setDmrvSteps(d.steps ?? []);
      setDmrvState("success");
    } catch (ex: unknown) {
      const { state, message } = friendlyError(ex, "Failed to load your verification status");
      setDmrvState(state); setDmrvMsg(message);
    }
  };

  const loadContracts = async () => {
    if (!account) return;
    setContractsState("loading");
    try {
      const d = await api.get<{ contracts: ContractRow[] }>(`/customers/${account.id}/contracts`);
      setContracts(d.contracts ?? []);
      setContractsState("success");
    } catch (ex: unknown) {
      const { state, message } = friendlyError(ex, "Failed to load your contracts");
      setContractsState(state); setContractsMsg(message);
    }
  };

  const selectTab = (t: Tab) => {
    setTab(t);
    if (t === "profile" && profileState === "idle") loadProfile();
    if (t === "payments" && paymentsState === "idle") loadPayments();
    if (t === "dmrv" && dmrvState === "idle") loadDmrv();
    if (t === "contracts" && contractsState === "idle") loadContracts();
  };

  // Auto-load the first tab once an account is found.
  useEffect(() => { if (account && profileState === "idle") loadProfile(); }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  const recordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account?.active_contract?.id) {
      setPaymentError("An active contract is required before recording a payment.");
      return;
    }
    const amountTzs = Number(paymentAmount);
    if (!Number.isFinite(amountTzs) || amountTzs <= 0) {
      setPaymentError("Enter a valid payment amount in TZS.");
      return;
    }
    if (!providerReference.trim()) {
      setPaymentError("Enter the provider or receipt reference.");
      return;
    }
    setPaymentBusy(true);
    setPaymentError("");
    setPaymentSuccess("");
    try {
      await api.post(
        "/payments",
        {
          customer_id: account.id,
          contract_id: account.active_contract.id,
          amount_cents: Math.round(amountTzs * 100),
          method: paymentMethod,
          provider_reference: providerReference.trim(),
        },
        crypto.randomUUID(),
      );
      setPaymentSuccess("Payment recorded. Contract and days remaining have been refreshed.");
      setPaymentAmount("");
      setProviderReference("");
      await Promise.all([loadProfile(), loadPayments()]);
    } catch (ex: unknown) {
      setPaymentError(ex instanceof Error ? ex.message : "Payment could not be recorded");
    } finally {
      setPaymentBusy(false);
    }
  };

  function SectionShell({ state, message, onRetry, children }: {
    state: SectionState;
    message: string;
    onRetry: () => void;
    children: React.ReactNode;
  }) {
    if (state === "loading" || state === "idle") return <div className="pg-state"><IconSpinner size={32} /><p>Loading…</p></div>;
    if (state === "forbidden") return <div className="pg-state"><IconInfo size={32} /><p>{message}</p></div>;
    if (state === "error") return (
      <div className="pg-state">
        <IconError size={32} /><p>{message}</p>
        <div className="pg-state-actions"><AppButton type="button" onClick={onRetry}>Retry</AppButton></div>
      </div>
    );
    return <>{children}</>;
  }

  return (
    <div>
      <div className="pg-list-header">
        <div>
          <h2>My Account</h2>
          <p className="pg-text-muted">
            {me ? `Signed in as ${[me.first_name, me.last_name].filter(Boolean).join(" ") || me.username}` : meError || "Loading your account…"}
          </p>
        </div>
      </div>

      {!account && (
        <AppCard title="Find your customer record">
          <p style={{ marginTop: 0 }}>
            Ask your agent for your customer number if you don't have it. You can also look yourself up by phone number.
          </p>
          <form onSubmit={lookup}>
            <div className="pg-two-col-form">
              <AppField label="Phone Number">
                <AppInput id="portal-phone" type="tel" placeholder="+255…" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={lookupBusy} />
              </AppField>
              <AppField label="Customer Number (optional)">
                <AppInput id="portal-customer-number" type="text" placeholder="CUST-…" value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value)} disabled={lookupBusy} />
              </AppField>
            </div>
            {lookupError && <span className="error" role="alert">{lookupError}</span>}
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={lookupBusy}>{lookupBusy ? <><IconSpinner size={14} /> Searching…</> : "Find My Account"}</AppButton>
            </div>
          </form>
        </AppCard>
      )}

      {account && (
        <>
          <AppCard>
            <div className="pg-stats-row" style={{ marginBottom: 0 }}>
              <div>
                <div className="pg-stat-label">Customer Number</div>
                <div className="pg-stat-value" style={{ fontSize: 18 }}>{account.customer_number}</div>
              </div>
              <div>
                <div className="pg-stat-label">Name</div>
                <div className="pg-stat-value" style={{ fontSize: 18 }}>{account.name}</div>
              </div>
              <div>
                <div className="pg-stat-label">Phone</div>
                <div className="pg-stat-value" style={{ fontSize: 18 }}>{account.phone}</div>
              </div>
            </div>
            <AppButton type="button" variant="secondary" style={{ marginTop: 12 }} onClick={() => setAccount(null)}>
              Search a different account
            </AppButton>
          </AppCard>

          <AppCard title="Record Payment">
            <form onSubmit={recordPayment}>
              <div className="pg-two-col-form">
                <AppField label="Amount (TZS)">
                  <AppInput
                    type="number"
                    min="1"
                    step="1"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    disabled={paymentBusy}
                    required
                  />
                </AppField>
                <AppField label="Method">
                  <AppSelect value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={paymentBusy}>
                    <option value="mobile_money">Mobile money</option>
                    <option value="ussd">USSD</option>
                    <option value="office_cash">Office cash</option>
                    <option value="agent_collection">Agent collection</option>
                  </AppSelect>
                </AppField>
              </div>
              <AppField label="Provider / receipt reference">
                <AppInput
                  value={providerReference}
                  onChange={(e) => setProviderReference(e.target.value)}
                  disabled={paymentBusy}
                  required
                />
              </AppField>
              {paymentError ? <AppAlert severity="error">{paymentError}</AppAlert> : null}
              {paymentSuccess ? <AppAlert severity="success">{paymentSuccess}</AppAlert> : null}
              <AppButton type="submit" disabled={paymentBusy || !account.active_contract}>
                {paymentBusy ? <><IconSpinner size={14} /> Recording…</> : "Record Payment"}
              </AppButton>
            </form>
          </AppCard>

          <div className="pg-tabs">
            <button type="button" className={`pg-tab${tab === "profile" ? " active" : ""}`} onClick={() => selectTab("profile")}>Contract</button>
            <button type="button" className={`pg-tab${tab === "payments" ? " active" : ""}`} onClick={() => selectTab("payments")}>Payment History</button>
            <button type="button" className={`pg-tab${tab === "dmrv" ? " active" : ""}`} onClick={() => selectTab("dmrv")}>Verification Status</button>
            <button type="button" className={`pg-tab${tab === "contracts" ? " active" : ""}`} onClick={() => selectTab("contracts")}>All Contracts</button>
          </div>

          {tab === "profile" && (
            <AppCard title="Contract Summary">
              <SectionShell state={profileState} message={profileMsg} onRetry={loadProfile}>
                {profile?.active_contract ? (
                  <table className="pg-table">
                    <tbody>
                      <tr><td>Contract Number</td><td>{profile.active_contract.contract_number}</td></tr>
                      <tr><td>Total Device Price</td><td>{profile.active_contract.total_price.toLocaleString()} TZS</td></tr>
                      <tr><td>Amount Paid</td><td>{profile.active_contract.amount_paid.toLocaleString()} TZS</td></tr>
                      <tr><td>Amount Remaining</td><td>{profile.active_contract.amount_remaining.toLocaleString()} TZS</td></tr>
                      <tr><td>Arrears</td><td>{profile.active_contract.arrears.toLocaleString()} TZS</td></tr>
                      <tr><td>Started</td><td>{profile.active_contract.start_date ? new Date(profile.active_contract.start_date).toLocaleDateString("en-GB") : "—"}</td></tr>
                      {(profile.devices ?? []).map((d) => (
                        <tr key={d.id}>
                          <td>Days remaining ({d.serial_number})</td>
                          <td>
                            {d.days_remaining != null
                              ? `${d.days_remaining} day${d.days_remaining === 1 ? "" : "s"}`
                              : d.paid_through
                                ? `Paid through ${new Date(d.paid_through).toLocaleDateString("en-GB")}`
                                : "Not activated yet"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>You don't have an active contract yet.</p>
                )}
              </SectionShell>
            </AppCard>
          )}

          {tab === "payments" && (
            <AppCard title="Payment History">
              <SectionShell state={paymentsState} message={paymentsMsg} onRetry={loadPayments}>
                {payments.length === 0 ? <p>No payments recorded yet.</p> : (
                  <table className="pg-table">
                    <thead><tr><th>Reference</th><th>Amount (TZS)</th><th>Method</th><th>Status</th><th>Date</th></tr></thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>{p.payment_reference}</td>
                          <td>{p.amount.toLocaleString()}</td>
                          <td>{p.method}</td>
                          <td>{p.status}</td>
                          <td>{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SectionShell>
            </AppCard>
          )}

          {tab === "dmrv" && (
            <AppCard title="Verification Status (dMRV)">
              <SectionShell state={dmrvState} message={dmrvMsg} onRetry={loadDmrv}>
                {dmrvSteps.length === 0 ? <p>No verification steps recorded yet.</p> : (
                  <table className="pg-table">
                    <thead><tr><th>Step</th><th>Status</th><th>Date</th></tr></thead>
                    <tbody>
                      {dmrvSteps.map((s) => (
                        <tr key={s.step}>
                          <td>{s.step}. {s.label}</td>
                          <td>{s.status}</td>
                          <td>{s.actioned_at ? new Date(s.actioned_at).toLocaleDateString("en-GB") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SectionShell>
            </AppCard>
          )}

          {tab === "contracts" && (
            <AppCard title="All Contracts">
              <SectionShell state={contractsState} message={contractsMsg} onRetry={loadContracts}>
                {contracts.length === 0 ? <p>No contracts on record.</p> : (
                  <table className="pg-table">
                    <thead><tr><th>Contract #</th><th>Amount Remaining (TZS)</th><th>Frequency</th><th>Status</th></tr></thead>
                    <tbody>
                      {contracts.map((c) => (
                        <tr key={c.id}>
                          <td>{c.contract_number}</td>
                          <td>{c.amount_remaining.toLocaleString()}</td>
                          <td>{c.payment_frequency}</td>
                          <td>{c.is_completed ? "Completed" : "Active"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SectionShell>
            </AppCard>
          )}
        </>
      )}
    </div>
  );
}
